import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { AvailabilityStatus } from '@fcom/dapi/api/models/availability-status';
import { NO_MEAL_VARIANT, NO_UPGR_MEAL_VARIANT, SeatCharacteristics } from '@fcom/dapi/interfaces/cart.interface';
import {
  SelectionsPerTraveler,
  ServiceCatalogSelections,
  ServiceCatalogServices,
  TravelerService,
} from '@fcom/dapi/interfaces/services.interface';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairAmount } from '@fcom/dapi/api/models/finnair-amount';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairItineraryItemType } from '@fcom/dapi/api/models/finnair-itinerary-item-type';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { FinnairPassengerServiceItem } from '@fcom/dapi/api/models/finnair-passenger-service-item';
import { FinnairPassengerServiceSelectionItem } from '@fcom/dapi/api/models/finnair-passenger-service-selection-item';
import { FinnairServiceBoundItem } from '@fcom/dapi/api/models/finnair-service-bound-item';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { FinnairServices } from '@fcom/dapi/api/models/finnair-services';
import { FinnairServiceSegmentItem } from '@fcom/dapi/api/models/finnair-service-segment-item';
import { SeatInfo } from '@fcom/dapi/api/models/seat-info';
import { SeatmapSvgAndControlData } from '@fcom/dapi/api/models/seatmap-svg-and-control-data';
import { SeatPrice } from '@fcom/dapi/api/models/seat-price';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { capitalize, isEmpty, uniqueBy, valuesOf } from '@fcom/core/utils/utils';
import { PreciseNumber } from '@fcom/core/utils/precise-number';
import { SeatMapFlightInfo, SeatMapSeatInfo } from '@fcom/common/interfaces/gtm.interface';
import { FlightSeatSelections } from '@fcom/common/interfaces/seat-map.interface';

import { getCMSTier } from '../../../utils/service.utils';
import { seatCharacteristicsOrder, seatTypeInfo, ServiceOverlaps } from '../interfaces/ancillaries-interface';
import { isAncillaryBundle, isBoundBasedCategory } from './category.utils';
import { serviceCategories } from './service-mapping.utils';

/**
 * Generates a list of individual service items matching the selected quantity, with prices applied according to the defined tiers.
 *
 * @param service - The service containing pricing tiers and selected quantity.
 * @returns A list of service items with specific prices for each tier.
 */
const mapTieredPricesToServices = (service: TravelerService) =>
  service.tieredPricePerItem
    .reduce((allTieredServices, tieredService) => {
      const servicesForTier = Array.from({ length: tieredService.maxNumberItems }, () => ({
        ...service,
        pricePerItem: tieredService.pricePerItem,
      }));
      return allTieredServices.concat(servicesForTier);
    }, [])
    .slice(0, service.quantity);

export const getDefaultMealVariant = (flight: FinnairItineraryItemFlight): string => {
  if (flight && (!flight.isShortHaul || flight.cabinClass === 'BUSINESS')) {
    return NO_UPGR_MEAL_VARIANT;
  }
  return NO_MEAL_VARIANT;
};

export const getSeatPrice = (seat: SeatInfo, travelerId: string): SeatPrice => {
  return seat?.prices?.find((p: SeatPrice) => p.travelerIds.indexOf(travelerId) !== -1);
};

export const getSeatInfoForTracking = (
  seatMap: SeatmapSvgAndControlData,
  seatElement: Element,
  activePaxId: string
): SeatMapSeatInfo => {
  const seatNo = seatElement.getAttribute('data-id');
  const seatInfo = seatMap.seats.find((s) => s.seatNumber === seatNo);
  const price = getSeatPrice(seatInfo, activePaxId);
  return {
    seatNo,
    seatType: getSeatType(seatInfo?.characteristics).className,
    price: price?.price?.amount,
    currency: price?.price?.currencyCode,
  };
};

export const getSeatMapFlightInfoFromCart = (
  cartOrOrder: FinnairCart | FinnairOrder
): SeatMapFlightInfo | undefined => {
  if (!cartOrOrder) {
    return undefined;
  }
  const bound = cartOrOrder.bounds[0];
  const itinerary = bound.itinerary.find(
    (i) => i.type === FinnairItineraryItemType.FLIGHT
  ) as FinnairItineraryItemFlight;
  const price = cartOrOrder.prices?.unpaid?.flight?.total.totalAmount;
  return {
    origin: bound.departure.locationCode,
    destination: bound.arrival.locationCode,
    departureTime: bound.departure.dateTime,
    acType: itinerary.aircraft.name,
    flightNo: itinerary.flightNumber,
    ticketType: bound.fareFamily.name,
    price: price?.amount,
    currency: price?.currencyCode,
  };
};

export const getSeatMapFlight = (flight: FinnairItineraryItemFlight): SeatMapFlightInfo | undefined => {
  if (!flight) {
    return undefined;
  }
  return {
    origin: flight.departure.locationCode,
    destination: flight.arrival.locationCode,
    departureTime: flight.departure.dateTime,
    acType: flight.aircraft?.name,
    flightNo: flight.flightNumber,
    ticketType: flight.bookingClass,
  };
};

export const getSeatType = (characteristics: string[] = []): { className: string; label: string; cmsKey: string } => {
  if (characteristics.length === 0) {
    return seatTypeInfo['regular'];
  }

  const orderedCharacteristics = [...characteristics].sort((a: SeatCharacteristics, b: SeatCharacteristics) => {
    const aIndex = seatCharacteristicsOrder.indexOf(a);
    const bIndex = seatCharacteristicsOrder.indexOf(b);

    // Deprioritise characteristics not in ordering array
    const aValue = aIndex === -1 ? seatCharacteristicsOrder.length + 1 : aIndex;
    const bValue = bIndex === -1 ? seatCharacteristicsOrder.length + 1 : bIndex;

    return aValue - bValue;
  });

  return seatTypeInfo[orderedCharacteristics[0]] || seatTypeInfo['regular'];
};

const getSeatPosition = (seatNumber: string | undefined): { row: number; column: number } | undefined => {
  const [, row, column] = /^(\d+)([A-Z])$/.exec(seatNumber ?? '') ?? [];
  return row ? { row: Number(row), column: column.charCodeAt(0) } : undefined;
};

export const findClosestAvailableSeatNumber = (
  seats: SeatInfo[],
  travelerId: string,
  fromSeatNumber: string,
  takenSeatNumbers: string[]
): string | undefined => {
  const from = getSeatPosition(fromSeatNumber);
  if (!from) {
    return undefined;
  }

  let closest: { seatNumber: string; distance: number; rowDistance: number } | undefined;
  seats.forEach((seat) => {
    const position = getSeatPosition(seat.seatNumber);
    if (
      !position ||
      seat.statuses?.[travelerId] !== AvailabilityStatus.AVAILABLE ||
      takenSeatNumbers.includes(seat.seatNumber)
    ) {
      return;
    }

    // A row step weighs double so a seat beside the passenger beats the same seat in the next row.
    const rowDistance = Math.abs(position.row - from.row);
    const distance = rowDistance * 2 + Math.abs(position.column - from.column);
    if (
      !closest ||
      distance < closest.distance ||
      (distance === closest.distance && rowDistance < closest.rowDistance)
    ) {
      closest = { seatNumber: seat.seatNumber, distance, rowDistance };
    }
  });

  return closest?.seatNumber;
};

export const hasTravelerService = (travelerServices: TravelerService | TravelerService[] | undefined): boolean =>
  Array.isArray(travelerServices) ? travelerServices.length > 0 : Boolean(travelerServices);

export const sumOfPricesForTravelersServiceSelection = (
  selections: SelectionsPerTraveler<TravelerService | TravelerService[]>
): Amount => {
  const prices: Amount[] = valuesOf(selections)
    .filter(Boolean)
    .reduce<TravelerService[]>((all: TravelerService[], service: TravelerService | TravelerService[]) => {
      const services = Array.isArray(service) ? service : [service];

      return all.concat(
        services
          // TODO: with next set of bundles, we need to change this if we want to show bundle price in subTotal. For example use bundle primary/secondary category to make the decision whether to filter or not.
          .filter((service) => !!service && SubCategory.SEAT_WIFI !== service.category)
          .flatMap((s) =>
            s.quantity > 1
              ? s.tieredPricePerItem?.length > 0
                ? mapTieredPricesToServices(s)
                : Array.from({ length: s.quantity }, () => s)
              : s
          )
      );
    }, [])
    .map((service) => service.pricePerItem);

  if (isEmpty(prices)) {
    return undefined;
  }

  const currencyCode: string = prices[0].currencyCode;
  const currencyCodesMatch = !prices.find((a: Amount) => a.currencyCode !== currencyCode);
  if (!currencyCodesMatch) {
    // Do not show price if we can't calculate it
    return undefined;
  }

  const amount: string = prices
    .map((p: Amount) => p.amount)
    .reduce((total: PreciseNumber, current: string) => total.add(PreciseNumber.fromString(current)), PreciseNumber.ZERO)
    .toString();
  return { amount, currencyCode };
};

export function filterServicesByTravelerIds(
  services: FinnairServices | undefined,
  travelerIds: string[]
): FinnairServices {
  if (!services || !travelerIds?.length) {
    return services ?? {};
  }

  const filterByTravelers = (items: FinnairServiceItem[] = []): FinnairServiceItem[] => {
    return items.map((item) => ({
      ...item,
      bounds: item.bounds.map((bound) => ({
        ...bound,
        segments: bound.segments.map((seg) => ({
          ...seg,
          passengers: seg.passengers.filter((p) => travelerIds.includes(p.id)),
        })),
      })),
    }));
  };

  return {
    ...services,
    included: filterByTravelers(services.included),
    unpaid: filterByTravelers(services.unpaid),
    pending: filterByTravelers(services.pending),
    unconfirmed: filterByTravelers(services.unconfirmed),
  };
}

const filterServicesByFunction = (
  serviceItems: FinnairServiceItem[],
  filterFunction: (service: FinnairPassengerServiceSelectionItem) => boolean
): FinnairServiceItem[] => {
  return serviceItems.reduce((allItems, serviceItem) => {
    if (
      serviceItem.bounds.some((b) =>
        b.segments.some((s) => s.passengers.some((p) => p.services.some((s) => filterFunction(s))))
      )
    ) {
      allItems.push({
        ...serviceItem,
        bounds: serviceItem.bounds.reduce((bounds, bound) => {
          if (bound.segments.some((s) => s.passengers.some((p) => p.services.some((s) => filterFunction(s))))) {
            const segments = bound.segments.reduce((segments, segment) => {
              if (segment.passengers.some((p) => p.services.some((s) => filterFunction(s)))) {
                const passengers = segment.passengers.reduce((passengers, passenger) => {
                  if (passenger.services.some((s) => filterFunction(s))) {
                    const services = passenger.services.filter((s) => filterFunction(s));
                    const totalPrice = calculateTotalPrice(services.map((s) => s.totalPrice));
                    const originalTotalPrice = calculateTotalPrice(services.map((s) => s.originalTotalPrice));

                    passengers.push({
                      ...passenger,
                      quantity: services.length,
                      totalPrice,
                      originalTotalPrice,
                      services,
                    });
                  }
                  return passengers;
                }, [] as FinnairPassengerServiceItem[]);
                const totalPrice = calculateTotalPrice(passengers.map((s) => s.totalPrice));
                const originalTotalPrice = calculateTotalPrice(passengers.map((s) => s.originalTotalPrice));
                segments.push({
                  ...segment,
                  quantity: passengers.reduce((count, passenger) => count + passenger.quantity, 0),
                  totalPrice,
                  originalTotalPrice,
                  passengers,
                });
              }
              return segments;
            }, [] as FinnairServiceSegmentItem[]);
            const divider = isBoundBasedCategory(serviceItem.category) ? segments.length : 1;
            const totalPrice = calculateTotalPrice(segments.map((s) => s.totalPrice));
            const originalTotalPrice = calculateTotalPrice(segments.map((s) => s.originalTotalPrice));
            bounds.push({
              ...bound,
              quantity: Math.floor(segments.reduce((count, segment) => count + segment.quantity, 0) / divider),
              totalPrice,
              originalTotalPrice,
              segments,
            });
          }
          return bounds;
        }, [] as FinnairServiceBoundItem[]),
      });
    }
    return allItems;
  }, [] as FinnairServiceItem[]);
};

const calculateTotalPrice = (prices: FinnairAmount[]): FinnairAmount | undefined => {
  const items = prices.filter(Boolean);
  if (items.length === 0) {
    return undefined;
  }

  const amount = items
    .reduce((totalPrice, price) => totalPrice.add(PreciseNumber.fromString(price.amount)), PreciseNumber.ZERO)
    .toString();

  return {
    amount,
    currencyCode: items[0].currencyCode,
  };
};

export const filterServices = (
  finnairServiceItems: FinnairServiceItem[] = [],
  filterFunction?: (service: FinnairPassengerServiceSelectionItem) => boolean
): FinnairServiceItem[] => {
  if (filterFunction) {
    return filterServicesByFunction([...finnairServiceItems], filterFunction);
  }

  return [...finnairServiceItems];
};

const isNotIncludedOrZeroPriced = (service: FinnairPassengerServiceSelectionItem): boolean => {
  return !(
    service.includedInTicketType ||
    service.includedInTierBenefit ||
    !service.totalPrice?.amount ||
    parseFloat(service.totalPrice?.amount) <= 0
  );
};

export const getServicesToShow = (
  services: FinnairServices | undefined,
  keyToShow: keyof Omit<FinnairServices, 'servicesOrder'>,
  removeIncludedInTicketTypeAndTierBenefitAndZeroPricedServices = false,
  showFallback = false
): FinnairServiceItem[] => {
  if (services?.[keyToShow]?.length) {
    return filterServices(
      services?.[keyToShow],
      removeIncludedInTicketTypeAndTierBenefitAndZeroPricedServices ? isNotIncludedOrZeroPriced : undefined
    );
  }
  if (showFallback) {
    return filterServices(
      services?.included,
      removeIncludedInTicketTypeAndTierBenefitAndZeroPricedServices ? isNotIncludedOrZeroPriced : undefined
    );
  }

  return [];
};

export const combineServices = <T extends FinnairServiceItem>(listOfServices: T[][]): T[] =>
  listOfServices.filter(Boolean).reduce(
    (combinedServices, services) =>
      services.reduce((allServices, service) => {
        const currentService = allServices.find((s) => s.category === service.category);
        if (!currentService) {
          allServices.push({ ...service });
        } else {
          currentService.bounds = [...currentService.bounds, ...service.bounds].reduce((allBounds, bound) => {
            const currentBound = allBounds.find((b) => b.id === bound.id);
            if (!currentBound) {
              allBounds.push({ ...bound });
            } else {
              currentBound.segments = [...currentBound.segments, ...bound.segments].reduce((allSegments, segment) => {
                const currentSegment = allSegments.find((s) => s.id === segment.id);
                if (!currentSegment) {
                  allSegments.push({ ...segment });
                } else {
                  currentSegment.passengers = [...currentSegment.passengers, ...segment.passengers].reduce(
                    (allPassengers, passenger) => {
                      const currentPassenger = allPassengers.find((p) => p.id === passenger.id);
                      if (!currentPassenger) {
                        allPassengers.push({ ...passenger });
                      } else {
                        currentPassenger.services = [...currentPassenger.services, ...passenger.services].filter(
                          uniqueBy((service) => service.id)
                        );
                      }
                      return allPassengers;
                    },
                    []
                  );
                }
                return allSegments;
              }, []);
              return allBounds;
            }
            return allBounds;
          }, []);
        }
        return allServices;
      }, combinedServices),
    []
  );

export const getPassengerTierLevel = (
  passengerId: string,
  passengerItems: FinnairPassengerItem[]
): string | undefined => {
  const passenger = passengerItems.find(({ id }) => id === passengerId);
  const tier = (passenger?.frequentFlyerCards || [])
    .find((c) => c.companyCode && c.companyCode.toLowerCase() === 'ay')
    ?.tierLevel?.toString();
  return tier ? capitalize(getCMSTier(tier)) : undefined;
};

/**
 * Checks whether an ancillary category has at least one segment with an eligible service in the
 * cart.
 */
export const hasCategoryGotEligibilityInAnySegment = (
  cart: FinnairCart,
  category: Category,
  offeredServices?: ServiceCatalogServices
): boolean => {
  if (!serviceCategories.includes(category)) {
    return true;
  }

  const categoryEligibilities = cart?.eligibilities?.serviceCatalog?.categories ?? [];
  const relevantEligibilities = categoryEligibilities.filter((c) => c.category === category);

  if (relevantEligibilities.length === 0) {
    return true;
  }

  const offeredFragmentIds =
    isAncillaryBundle(category) && offeredServices ? new Set(Object.keys(offeredServices)) : undefined;

  return relevantEligibilities.some((categoryElig) =>
    Object.entries(categoryElig.services ?? {}).some(
      ([fragmentId, servicesForBound]) =>
        (!offeredFragmentIds || offeredFragmentIds.has(fragmentId)) &&
        Object.values(servicesForBound).some((eligibility) => eligibility.isAllowedToUse)
    )
  );
};

export const getServiceOverlapStateForPaxForFlight = (
  overlapSelections: ServiceCatalogSelections<TravelerService[]>,
  flightId: string,
  paxId: string,
  dismissed: boolean,
  seatSelections?: FlightSeatSelections
): ServiceOverlaps => {
  const services = [flightId, `BND-${flightId}`].flatMap((fragmentId) => overlapSelections[fragmentId]?.[paxId] ?? []);
  const selectedSeat = seatSelections?.[paxId];
  const seat = selectedSeat?.seatNumber ? selectedSeat : undefined;

  return { services, seat, active: services.length > 0 && !dismissed };
};

/** Marks the history entry pushed when an ancillary subview opens, so back closes the subview instead of leaving the step */
export const SUBVIEW_HISTORY_STATE_KEY = 'ancillarySubview';

export const isSubviewHistoryEntry = (state: unknown): boolean =>
  Boolean((state as Record<string, unknown> | null)?.[SUBVIEW_HISTORY_STATE_KEY]);

const SPORTS_EQUIPMENT_ORDER: SubCategory[] = [SubCategory.SKIS, SubCategory.GOLF, SubCategory.BIKE, SubCategory.SURF];

export const sortSportsEquipmentServices = <T extends { subCategory?: SubCategory }>(services: T[]): T[] => {
  const input = services ?? [];

  const pickInOrder = SPORTS_EQUIPMENT_ORDER.flatMap((subCategory) =>
    input.filter((s) => s.subCategory === subCategory)
  );
  const remaining = input.filter((s) => !SPORTS_EQUIPMENT_ORDER.includes(s.subCategory));

  return [...pickInOrder, ...remaining];
};
